import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { IMAGES, STATS as FALLBACK_STATS, TESTIMONIALS, PARTNERS } from "../lib/content";
import { SIGNATURE_PROGRAMS as PROGRAMS } from "../lib/signaturePrograms";
import Newsletter from "../components/Newsletter";
import { NewHomeBanner } from "../components/NewHomeBanner";
import { useQuery } from "@tanstack/react-query";
import { getEvents, getStories, getStats } from "../lib/api";

function StatCard({ label, value, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: i * 0.08 }}
      className="relative border border-white/10 p-8 md:p-10"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="font-display text-5xl md:text-6xl font-bold text-[#F5B90A] leading-none">{value}</p>
      <p className="mt-3 text-sm text-white/70 uppercase tracking-widest">{label}</p>
    </motion.div>
  );
}

export default function Home() {
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: getEvents });
  const { data: stories = [] } = useQuery({ queryKey: ["stories"], queryFn: () => getStories() });
  const { data: stats = FALLBACK_STATS } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  return (
    <>
      <NewHomeBanner />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="hero-section">
        <div className="noise" />
        <div className="absolute inset-0">
          <img src={IMAGES.heroMentoring} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        </div>

        <div className="container-x relative py-24 md:py-32 grid gap-10 md:grid-cols-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">A 501(c)(3) Nonprofit · Est. Clayton County, GA</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              WE'RE NOT JUST MENTORING BOYS.<br />
              <span className="italic text-[#F5B90A]">We're building future leaders.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-white/80 leading-relaxed">
              At Change Center Mentoring Group, we help boys discover their purpose, develop healthy minds, make better choices, and build the skills to lead successful lives. Through mentorship, prevention, education, and community partnerships, we're changing this generation one young man at a time.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/enroll" className="btn-gold" data-testid="hero-enroll-btn">Enroll Your Child <ArrowRight className="ml-2 h-4 w-4" /></Link>
              <Link to="/programs" className="btn-outline-light" data-testid="hero-programs-btn">Explore Our Programs</Link>
              <Link to="/become-a-mentor" className="btn-outline-light" data-testid="hero-mentor-btn">Become a Mentor</Link>
              <Link to="/partnerships" className="btn-outline-light" data-testid="hero-partner-btn">Partner With Us</Link>
              <Link to="/donate" className="btn-primary" data-testid="hero-donate-btn">Donate Today</Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative md:col-span-5 flex justify-center md:justify-end"
          >
            <div className="relative">
              <div className="absolute inset-8 bg-[#F5B90A]/15 rounded-full blur-3xl" />
              <span className="absolute -top-2 left-2 z-10 rounded-full bg-[#F5B90A] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-black shadow-lg">Meet Coach Kaf</span>
              <img
                src="/coach-kaf-hero.png"
                alt="Coach Kaf — mascot of The Change Center Mentoring Group"
                className="relative h-[440px] md:h-[560px] w-auto drop-shadow-[0_25px_50px_rgba(245,185,10,0.35)]"
                data-testid="hero-coach-kaf"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* HERO VIDEO */}
      <section className="relative bg-[#0A0A0A] py-16 md:py-20 overflow-hidden" data-testid="hero-video-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#111]" />
        <div className="noise" />
        <div className="container-x relative">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="overline text-[#F5B90A]">A Message To Our Community</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-white">Hear it directly from us.</h2>
            </div>
            <div className="relative rounded-xl overflow-hidden ring-1 ring-white/15 shadow-[0_30px_80px_-20px_rgba(107,33,168,0.6)]">
              <video
                className="w-full h-auto block"
                controls
                playsInline
                preload="metadata"
                poster="/videos/hero-poster.jpg"
                data-testid="hero-video"
              >
                <source src="/videos/hero.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* MEET COACH KAF */}
      <section className="relative overflow-hidden bg-[#FDFBF7] border-b border-black/10">
        <div className="container-x py-20 md:py-24 grid gap-10 md:grid-cols-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 relative flex justify-center"
          >
            <div className="absolute inset-6 bg-[#6B21A8]/10 rounded-full blur-3xl" />
            <img
              src="/coach-kaf-portrait.png"
              alt="Coach Kaf portrait"
              className="relative h-[380px] md:h-[480px] w-auto drop-shadow-[0_20px_40px_rgba(107,33,168,0.25)]"
              data-testid="coach-kaf-meet"
            />
          </motion.div>
          <div className="md:col-span-7">
            <span className="overline text-[#6B21A8]">Meet your coach</span>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold tracking-tight leading-tight">
              I'm <span className="italic text-[#6B21A8]">Coach Kaf.</span><br />I got you.
            </h2>
            <p className="mt-6 max-w-lg text-lg text-black/70 leading-relaxed">
              Every young man in our program gets a coach in their corner — showing up, holding the line, celebrating the wins. That's what I'm here for. Whether you're a mentee, a parent, or thinking about mentoring — I'll help you find your way around.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/programs" className="btn-primary" data-testid="kaf-explore-programs">Explore programs</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("open-kaf-chat"))}
                className="btn-outline"
                data-testid="kaf-ask-me"
              >
                Ask me anything
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE PROGRAM — STRONG MINDS HIGHLIGHT */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#6B21A8] via-[#4C1374] to-[#0A0A0A] text-white" data-testid="signature-highlight">
        <div className="noise" />
        <div className="container-x py-20 md:py-28 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <span className="inline-block bg-[#F5B90A] text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">★ Signature Program</span>
            <h2 className="mt-6 font-display text-4xl md:text-6xl font-bold leading-tight">
              CHANGE CHOICES:<br />
              <span className="italic text-[#F5B90A]">Strong Minds.</span>
            </h2>
            <p className="mt-4 font-display text-xl md:text-2xl text-[#F5B90A]/90 italic">
              Strong Minds. Wise Choices. Brighter Futures.
            </p>
            <p className="mt-6 max-w-xl text-lg text-white/85 leading-relaxed">
              Our integrated 24-week youth mental wellness, substance-use prevention, and healthy decision-making program for boys ages 10–17.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/strong-minds" className="btn-gold" data-testid="home-sm-learn">Learn About Strong Minds <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/strong-minds-center" className="btn-outline-white" data-testid="home-sm-center">Resource Center</Link>
              <Link to="/partnerships" className="btn-outline-white" data-testid="home-sm-school">For Schools</Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Weeks", value: "24" },
                { label: "Ages", value: "10–17" },
                { label: "Pillars", value: "3" },
                { label: "Topics", value: "25+" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg bg-white/5 border border-white/15 p-6 backdrop-blur-sm">
                  <p className="font-display text-5xl font-bold text-[#F5B90A] leading-none">{s.value}</p>
                  <p className="mt-2 text-sm text-white/70 uppercase tracking-widest">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION LINE */}
      <section className="border-b border-black/10 bg-[#FDFBF7]">
        <div className="container-x py-16 md:py-24 grid gap-10 md:grid-cols-12 items-start">
          <div className="md:col-span-4">
            <span className="gold-line mb-5" />
            <p className="overline text-[#6B21A8]">Our Mission</p>
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-3xl md:text-5xl leading-tight text-black">
              We walk beside young men through their most formative years — with brotherhood, structure, and belief — until they walk into their next chapter as leaders.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="stats-section">
        <div className="noise" />
        <div className="container-x py-20">
          <div className="mb-12 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="overline text-[#F5B90A]">Our Impact</span>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">Numbers that mean lives.</h2>
            </div>
            <p className="max-w-md text-white/70">Every number here is a young man, a family, a school, and a community that walked with us.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border-t border-white/10">
            {stats.map((s, i) => <StatCard key={s.label} {...s} i={i} />)}
          </div>
        </div>
      </section>

      {/* PROGRAMS PREVIEW */}
      <section className="bg-white">
        <div className="container-x py-20 md:py-28">
          <div className="grid gap-6 md:grid-cols-12 items-end mb-12">
            <div className="md:col-span-8">
              <span className="overline text-[#6B21A8]">What we teach</span>
              <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold tracking-tight">Ten programs. One mission.</h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/programs" className="inline-flex items-center gap-2 font-semibold text-[#6B21A8] hover:gap-3 transition-all" data-testid="programs-see-all">
                See all programs <ArrowUpRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROGRAMS.slice(0, 6).map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link to={`/programs/${p.slug}`} className="card-editorial block p-8 h-full group" data-testid={`program-card-${p.slug}`}>
                  <p.icon className="h-8 w-8 text-[#6B21A8]" strokeWidth={1.75} />
                  <h3 className="mt-6 font-display text-2xl font-semibold text-black">{p.title}</h3>
                  <p className="mt-3 text-sm text-black/70 leading-relaxed">{p.summary}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-black/80 group-hover:text-[#6B21A8]">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORY FEATURE */}
      {stories[0] && (
        <section className="bg-[#FDFBF7] border-y border-black/10">
          <div className="container-x py-20 md:py-28 grid gap-10 md:grid-cols-12 items-center">
            <div className="md:col-span-6 order-2 md:order-1">
              <span className="overline text-[#6B21A8]">Success Story</span>
              <Quote className="mt-6 h-10 w-10 text-[#F5B90A]" />
              <p className="mt-6 font-display text-2xl md:text-4xl leading-snug text-black">
                “{stories[0].quote}”
              </p>
              <p className="mt-8 text-sm text-black/70">
                <span className="font-semibold">{stories[0].student_name}</span> · {stories[0].school} · Class of {stories[0].year}
              </p>
              <Link to="/success-stories" data-testid="feature-story-link" className="mt-8 inline-flex items-center gap-2 font-semibold text-[#6B21A8] hover:gap-3 transition-all">
                Read more stories <ArrowUpRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="md:col-span-6 order-1 md:order-2">
              <div className="relative overflow-hidden border border-black/10">
                <img src={stories[0].image} alt="" className="aspect-[4/5] h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* UPCOMING EVENTS */}
      <section className="bg-white">
        <div className="container-x py-20 md:py-28">
          <div className="mb-10 flex items-end justify-between gap-4">
            <div>
              <span className="overline text-[#6B21A8]">What's next</span>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">Upcoming events</h2>
            </div>
            <Link to="/events" data-testid="events-see-all" className="hidden md:inline-flex items-center gap-2 font-semibold text-[#6B21A8] hover:gap-3 transition-all">
              View calendar <ArrowUpRight className="h-5 w-5" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {events.slice(0, 3).map((e) => (
              <Link key={e.id} to="/events" className="card-editorial block group" data-testid={`event-card-${e.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={e.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <p className="overline text-[#6B21A8]">{e.category}</p>
                  <h3 className="mt-3 font-display text-xl font-semibold text-black">{e.title}</h3>
                  <p className="mt-2 text-sm text-black/60">{new Date(e.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · {e.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#4A154B] text-white">
        <div className="container-x py-20 md:py-28">
          <div className="mb-14 max-w-2xl">
            <span className="overline text-[#F5B90A]">Voices</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">The people who make it real.</h2>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="border border-white/15 p-8"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-8 w-8 text-[#F5B90A]" />
                <p className="mt-6 font-display text-xl leading-snug">“{t.quote}”</p>
                <p className="mt-8 text-sm text-white/70">
                  <span className="font-semibold text-white">{t.name}</span> — {t.role}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="bg-[#F5B90A]">
        <div className="container-x py-20 md:py-28 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl md:text-6xl font-bold text-black leading-tight">
              $50 feeds a mentee for a month.<br />
              <span className="italic">$500 changes a life.</span>
            </h2>
            <p className="mt-6 max-w-xl text-black/80 text-lg">
              Every dollar goes directly into programming, meals, transportation, and mentor stipends. No overhead theatre.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/donate" className="btn-primary" data-testid="cta-donate-btn">Donate now <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="bg-white border-t border-black/10">
        <div className="container-x py-16">
          <p className="overline text-[#6B21A8] mb-8">Partners in the work</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
            {PARTNERS.slice(0, 8).map((p) => (
              <div key={p.name} className="border-l-2 border-[#F5B90A] pl-4">
                <p className="font-display text-lg font-semibold text-black leading-tight">{p.name}</p>
                <p className="text-xs uppercase tracking-widest text-black/50 mt-1">{p.type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#FDFBF7] border-t border-black/10">
        <div className="container-x py-20 md:py-24 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-6">
            <span className="overline text-[#6B21A8]">Stay connected</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-black">Monthly stories from the brotherhood.</h2>
          </div>
          <div className="md:col-span-6">
            <Newsletter />
            <p className="mt-3 text-xs text-black/50">We'll never spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>
    </>
  );
}
